import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, SquarePen } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { currentLocale } from '../components/numericFormat';
import { EMPTY } from './perspectiveValues';
import ParcelMap from './ParcelMap';
import ParcelAssignmentDialog from './ParcelAssignmentDialog';
import { PerspectiveScope } from './sitePerspectiveApi';
import { PhaseParcelResult, PhaseParcelState } from './phaseParcelsApi';

/**
 * The parcels of the plot, as a map.
 *
 * They were a table, which answered the wrong question. Which Flurstücke make up a plot is decided
 * by looking at where they lie — the yard behind the house, the strip along the road — and a column
 * of numbers like "3124/2" is exactly the form in which that cannot be judged. The outlines carry
 * the same three states the table's column did: solid for what belongs to the plot, dashed for what
 * the estimator has not settled.
 *
 * The total area stays written out. It is what the rest of the perspective computes with, and a
 * picture does not add up.
 */

interface Properties {
    parcelState?: PhaseParcelState;
    /** Needed to write a decision; absent while no property is picked. */
    scope?: PerspectiveScope | null;
    /** The building's outline, drawn under the parcels for orientation. */
    footprint?: any;
    /** Takes the plot back after a decision, so every view of it agrees. */
    onParcelsChanged?: (result: PhaseParcelResult) => void;
    /** False where the phase is not activated or nothing is selected. */
    canEdit?: boolean;
}

export default function PhaseParcels({
    parcelState,
    scope,
    footprint,
    onParcelsChanged,
    canEdit,
}: Properties) {
    const { t } = useTranslation();
    const [assigning, setAssigning] = useState(false);

    if (!parcelState) return null;

    const { rows, siteAreaMin, siteAreaMax, loading, error } = parcelState;

    if (loading) {
        return (
            <span role="status" className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                {t('global.loading')} …
            </span>
        );
    }
    if (error) return <span className="text-sm text-destructive">{error}</span>;

    const area = (value: number) => new Intl.NumberFormat(currentLocale()).format(Math.round(value));
    const editable = canEdit && !!scope && !!onParcelsChanged;

    return (
        <>
            {/* Even with no parcel at all the map is offered, because that is the case where one
                most needs adding: an estimate that found nothing leaves a plot to be drawn by hand. */}
            {rows.length === 0 && !footprint ? (
                <div className="text-sm text-muted-foreground">{t('portfolioDashboard.parcels.none')}</div>
            ) : (
                <ParcelMap rows={rows} footprint={footprint} height={260} />
            )}

            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm text-muted-foreground">
                    {siteAreaMin === null ? (
                        rows.length === 0 ? (
                            t('portfolioDashboard.parcels.none')
                        ) : (
                            EMPTY
                        )
                    ) : siteAreaMax !== null && siteAreaMax > siteAreaMin ? (
                        t('portfolioDashboard.parcels.summaryRange', {
                            min: area(siteAreaMin),
                            max: area(siteAreaMax),
                        })
                    ) : (
                        t('portfolioDashboard.parcels.summary', { area: area(siteAreaMin) })
                    )}
                </div>

                {editable ? (
                    <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setAssigning(true)}>
                        <SquarePen size={14} />
                        {t('portfolioDashboard.parcels.assign')}
                    </Button>
                ) : (
                    <></>
                )}
            </div>

            {editable ? (
                <ParcelAssignmentDialog
                    open={assigning}
                    onDismiss={() => setAssigning(false)}
                    scope={scope ?? null}
                    rows={rows}
                    siteAreaMin={siteAreaMin}
                    siteAreaMax={siteAreaMax}
                    footprint={footprint}
                    onChanged={onParcelsChanged!}
                />
            ) : (
                <></>
            )}
        </>
    );
}
